import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

const LANGUAGE_KEY = '@sms_language';

type Language = 'ar' | 'en';

interface LanguageContextType {
  language: Language;
  changeLanguage: (lang: Language) => Promise<void>;
  t: (key: string, options?: Record<string, unknown>) => any;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Default to Arabic
  const [language, setLanguage] = useState<Language>('ar');
  const [isInitialized, setIsInitialized] = useState(false);
  const { t: i18nT } = useTranslation();

  // Initialize language from storage
  useEffect(() => {
    const initLanguage = async () => {
      try {
        const savedLanguage = await AsyncStorage.getItem(LANGUAGE_KEY);
        if (savedLanguage === 'en' || savedLanguage === 'ar') {
          setLanguage(savedLanguage);
          await i18n.changeLanguage(savedLanguage);
        } else {
          // Default to Arabic
          await i18n.changeLanguage('ar');
        }
      } catch (error) {
        console.error('Error initializing language:', error);
        await i18n.changeLanguage('ar');
      } finally {
        setIsInitialized(true);
      }
    };

    initLanguage();
  }, []);

  const changeLanguage = async (lang: Language) => {
    try {
      // Save to storage
      await AsyncStorage.setItem(LANGUAGE_KEY, lang);
      // Update i18n
      await i18n.changeLanguage(lang);
      // Update state
      setLanguage(lang);
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };

  // Translation function with fallback
  const t = (key: string, options?: Record<string, unknown>): any => {
    const result = i18nT(key, options as any);
    // When returnObjects is true, pass through arrays/objects from i18next
    if (options?.returnObjects) return result;
    return typeof result === 'string' ? result : key;
  };

  if (!isInitialized) {
    return null;
  }

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
